import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IDatasource } from 'ag-grid-community';
import { BsCheckLg, BsPlusLg, BsShieldExclamation } from 'react-icons/bs';
import { TbCopy } from 'react-icons/tb';
import { GridSearchInput } from '../../components/shared/GridToolbar';
import StatusFilterTabs from '../../components/shared/StatusFilterTabs';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { GRID_HINT_TOOLTIP_CLASS } from '../../components/shared/agGridCells';
import { DataGrid, type GridApi, type GridReadyEvent } from '../../components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { cn } from '../../lib/utils';
import { showToast } from '../../store/toastStore';
import { copyToClipboard } from '../../utils/clipboard';
import { createDecisionColumnDefs, createBlocklistColumnDefs } from './table/columnDefs';
import {
  FRAUD_GRID_DEFAULT_COL_DEF,
  fraudGridOptions,
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
} from './table/gridOptions';
import {
  addBlocklistEntry,
  fetchBlocklist,
  fetchFraudDecisions,
  fetchFraudMode,
  fetchFraudSummary,
  removeBlocklistEntry,
  setFraudMode,
} from './api/api';
import FraudModeSelect from './components/FraudModeSelect';
import type { FraudBlocklistEntry, FraudDecisionRow, FraudSummary } from './models/types';
import './FraudMonitoring.css';

const FRAUD_VIEW_TABS = [
  { value: 'decisions', label: 'Decisions' },
  { value: 'blocklist', label: 'Blocklist' },
] as const;

const DAY_TABS = [
  { value: '1', label: '24h' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
] as const;
const DECISION_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'BLOCK', label: 'Block' },
  { value: 'REVIEW', label: 'Review' },
  { value: 'ALLOW', label: 'Allow' },
] as const;
const ENTRY_TYPES = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'CARD_HASH', label: 'Card fingerprint' },
  { value: 'IP', label: 'IP' },
] as const;

function DetailCopyValue({ value, mono }: { value: string; mono: boolean }) {
  const [copied, setCopied] = useState(false);
  const canCopy = value !== '—';

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const ok = await copyToClipboard(value);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <dd className={cn('mt-0.5 flex min-w-0 items-start gap-1 text-[13px] text-foreground', mono && 'font-mono text-xs')}>
      <span className="min-w-0 break-all">{value}</span>
      {canCopy ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              aria-label="Copy"
              onClick={handleCopy}
            >
              {copied ? (
                <BsCheckLg className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <TbCopy className="h-3.5 w-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className={GRID_HINT_TOOLTIP_CLASS}>
            {copied ? 'Copied' : 'Copy'}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </dd>
  );
}

function decisionChipClass(decision?: string | null) {
  switch ((decision ?? '').toLowerCase()) {
    case 'block':
      return 'bg-red-500/15 text-red-600 dark:text-red-400';
    case 'review':
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
    default:
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
  }
}

export default function FraudMonitoring() {
  const [tab, setTab] = useState<'decisions' | 'blocklist'>('decisions');
  const [days, setDays] = useState(7);
  const [decisionFilter, setDecisionFilter] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [summary, setSummary] = useState<FraudSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<FraudDecisionRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  // Enforce/shadow is the source of truth for the toggle; null until the mode endpoint answers.
  const [enforcing, setEnforcing] = useState<boolean | null>(null);
  const [modeSaving, setModeSaving] = useState(false);
  const [confirmEnforceOpen, setConfirmEnforceOpen] = useState(false);

  const [newType, setNewType] = useState<string>('EMAIL');
  const [newValue, setNewValue] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newExpiryHours, setNewExpiryHours] = useState<string>('24');
  const [saving, setSaving] = useState(false);

  // Server-side pagination via AG Grid's Infinite Row Model — mirrors TransactionMonitoring. The
  // grid fetches one page at a time through `tableDatasource`; `gridApiRef` lets the filter-change
  // effect reset it, and `tableStateRef` hands the stable datasource the latest tab/filters without
  // rebuilding it (the Infinite Row Model needs a stable datasource reference).
  const gridApiRef = useRef<GridApi | null>(null);
  const tableStateRef = useRef({ tab, decisionFilter, search: debouncedSearch, days });

  // Debounce the search box so typing doesn't refetch on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Summary tiles — fetched independently of the grid, refreshed when the day window changes.
  const loadSummary = useCallback(() => {
    fetchFraudSummary(days).then(setSummary).catch(() => {});
  }, [days]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const tableDatasource = useMemo<IDatasource>(
    () => ({
      getRows: async (params) => {
        const pageSize = params.endRow - params.startRow;
        const page = Math.floor(params.startRow / pageSize);
        const s = tableStateRef.current;
        setLoading(true);
        setLoadError(null);
        try {
          const result = s.tab === 'decisions'
            ? await fetchFraudDecisions({
                decision: s.decisionFilter, search: s.search, days: s.days, page, size: pageSize,
              })
            : await fetchBlocklist({ search: s.search, page, size: pageSize });
          params.successCallback(result.content as any, result.totalElements);
        } catch (err: any) {
          setLoadError(
            err?.message || (tableStateRef.current.tab === 'decisions'
              ? 'Could not load risk decisions.'
              : 'Could not load the blocklist.'),
          );
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    }),
    [],
  );

  // Tab / filter / search / day-window changed → point the ref at the latest state and reset the
  // infinite cache back to row 0 (re-assigning the datasource is the documented full reset).
  useEffect(() => {
    tableStateRef.current = { tab, decisionFilter, search: debouncedSearch, days };
    gridApiRef.current?.setGridOption('datasource', tableDatasource);
  }, [tab, decisionFilter, debouncedSearch, days, tableDatasource]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchFraudMode()
      .then((v) => !cancelled && setEnforcing(v))
      .catch(() => !cancelled && setEnforcing(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply a mode change. Turning enforcement ON is confirmed first (it starts refusing real
  // payments); turning it back to shadow is immediate. Reloads after so the tiles/subtitle, which
  // read shadowMode off the decisions, stay in step.
  const applyMode = useCallback(
    async (enforce: boolean) => {
      setModeSaving(true);
      try {
        const now = await setFraudMode(enforce);
        setEnforcing(now);
        showToast(
          now
            ? 'Enforce mode is on — risky payments will now be refused.'
            : 'Shadow mode is on — payments are screened but never refused.',
          'success',
        );
        // Tiles read shadowMode off the summary; the grid rows carry it too — refresh both.
        loadSummary();
        gridApiRef.current?.refreshInfiniteCache();
      } catch (err: any) {
        showToast(err?.message || 'Could not change the mode.', 'error');
      } finally {
        setModeSaving(false);
      }
    },
    [loadSummary],
  );

  const handleModeToggle = useCallback(
    (next: boolean) => {
      if (modeSaving || next === enforcing) return;
      if (next) setConfirmEnforceOpen(true); // enabling enforcement — confirm first
      else void applyMode(false);
    },
    [modeSaving, enforcing, applyMode],
  );

  const handleRemove = useCallback(async (entry: FraudBlocklistEntry) => {
    try {
      await removeBlocklistEntry(entry.id);
      showToast(`${entry.entryValue} removed from the blocklist.`, 'success');
      gridApiRef.current?.refreshInfiniteCache();
    } catch (err: any) {
      showToast(err?.message || 'Could not remove the entry.', 'error');
    }
  }, []);

  const submitEntry = useCallback(
    async (type: string, value: string, reason: string, hours: string) => {
      if (!value.trim()) {
        showToast('Enter a value to block.', 'error');
        return false;
      }
      setSaving(true);
      try {
        const parsed = Number(hours);
        await addBlocklistEntry({
          entryType: type,
          entryValue: value.trim(),
          reason: reason.trim() || undefined,
          expiresInHours: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
        });
        showToast('Added to the blocklist.', 'success');
        if (tab === 'blocklist') gridApiRef.current?.refreshInfiniteCache();
        return true;
      } catch (err: any) {
        showToast(err?.message || 'Could not add the entry.', 'error');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [tab],
  );

  const handleAdd = useCallback(async () => {
    const ok = await submitEntry(newType, newValue, newReason, newExpiryHours);
    if (ok) {
      setNewValue('');
      setNewReason('');
      setAddOpen(false);
    }
  }, [submitEntry, newType, newValue, newReason, newExpiryHours]);

  /** One-click blocking straight from a decision — the action you actually want after reading one. */
  const blockFromDecision = useCallback(
    async (type: string, value?: string | null) => {
      if (!value) return;
      const ok = await submitEntry(type, value, 'Blocked from a risk decision', '24');
      if (ok) setSelected(null);
    },
    [submitEntry],
  );

  const decisionColumns = useMemo(() => createDecisionColumnDefs(), []);
  const blocklistColumns = useMemo(() => createBlocklistColumnDefs(handleRemove), [handleRemove]);

  // Only the Why column opens the decision detail. Copy buttons and the rest of the row stay put.
  const gridOptionsWithRowClick = useMemo(
    () => ({
      ...fraudGridOptions,
      onCellClicked: (event: any) => {
        if (tab !== 'decisions') return;
        const colId = event.column?.getColId?.() ?? event.colDef?.field;
        if (colId !== 'reasons') return;
        setSelected(event.data as FraudDecisionRow);
      },
    }),
    [tab],
  );

  const maxReasonCount = summary?.topReasons?.[0]?.count ?? 0;
  const periodLabel = days === 1 ? 'the last 24 hours' : `the last ${days} days`;

  return (
    <ListConsolePage
      title="Risk Screening"
      error={loadError}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <StatusFilterTabs
            tabs={[...FRAUD_VIEW_TABS]}
            value={tab}
            onChange={(value) => setTab(value as 'decisions' | 'blocklist')}
            disabled={loading}
            className="w-auto shrink-0"
          />
          {tab === 'decisions' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="tm-console-action-btn tm-console-action-btn--outline gap-1.5"
              onClick={() => setReasonsOpen(true)}
            >
              <BsShieldExclamation className="h-3 w-3 shrink-0" aria-hidden />
              Why Flagged
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              className="tm-console-action-btn gap-1.5"
              onClick={() => setAddOpen(true)}
            >
              <BsPlusLg className="h-3 w-3 shrink-0" aria-hidden />
              Add Entry
            </Button>
          )}
          <FraudModeSelect
            value={enforcing ? 'enforcing' : 'shadow'}
            onChange={(next) => handleModeToggle(next === 'enforcing')}
            disabled={enforcing === null || modeSaving}
          />
        </div>
      }
    >
      <ListConsoleGridSection
        toolbarLeft={
          tab === 'decisions' ? (
            <div className="flex flex-wrap items-center gap-3">
              <StatusFilterTabs
                tabs={DECISION_TABS}
                value={decisionFilter}
                onChange={setDecisionFilter}
                disabled={loading}
                className="w-auto shrink-0"
              />
              <StatusFilterTabs
                tabs={DAY_TABS}
                value={String(days)}
                onChange={(value) => setDays(Number(value))}
                disabled={loading}
                className="w-auto shrink-0"
              />
            </div>
          ) : undefined
        }
        toolbarRight={
          <div className="tm-toolbar-controls">
            <GridSearchInput
              value={search}
              onValueChange={setSearch}
              placeholder={
                tab === 'decisions' ? 'Email, transaction, order or IP' : 'Search blocked values'
              }
              disabled={loading}
              className="tm-toolbar-search"
              aria-label={tab === 'decisions' ? 'Search decisions' : 'Search blocklist'}
            />
          </div>
        }
      >
        <div className={tab === 'decisions' ? 'fraud-grid-clickable min-h-0 flex-1' : 'min-h-0 flex-1'}>
          <DataGrid
            className={LIST_CONSOLE_AG_GRID_CLASS}
            height="100%"
            includeAuditColumns={false}
            rowModelType="infinite"
            datasource={tableDatasource}
            onGridReady={handleGridReady}
            columnDefs={(tab === 'decisions' ? decisionColumns : blocklistColumns) as any}
            defaultColDef={FRAUD_GRID_DEFAULT_COL_DEF}
            gridOptions={gridOptionsWithRowClick}
            pagination
            paginationPageSize={GRID_PAGE_SIZE}
            paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
            loading={loading}
          />
        </div>
      </ListConsoleGridSection>

      {/* Decision detail: every reason in full, plus the two actions worth taking right here. */}
      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5">
              <span
                className={cn(
                  'inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold tracking-wide',
                  decisionChipClass(selected?.decision),
                )}
              >
                {selected?.decision}
              </span>
              <span className="text-sm font-semibold tabular-nums text-muted-foreground">
                Risk score {selected?.score ?? 0}/100
              </span>
            </DialogTitle>
            <DialogDescription>
              {selected?.createdAt ? new Date(selected.createdAt).toLocaleString() : ''}
              {selected?.shadowMode ? ' · recorded only, not enforced' : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            {(selected?.reasons ?? []).length ? (
              (selected?.reasons ?? []).map((reason) => (
                <div
                  key={reason}
                  className="rounded-md border-l-[3px] border-amber-500 bg-amber-500/10 px-2.5 py-2 text-[13px] text-foreground"
                >
                  {reason}
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No rules fired — this payment looked ordinary.</p>
            )}
          </div>

          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(
              [
                ['Merchant', selected?.merchantName || (selected?.merchantId != null ? String(selected.merchantId) : '') || '—', false, false],
                ['Email', selected?.customerEmail || '—', false, true],
                [
                  'Amount',
                  selected?.amount != null
                    ? `${Number(selected.amount).toFixed(2)} ${selected.currency ?? ''}`.trim()
                    : '—',
                  false,
                  false,
                ],
                ['Caller IP', selected?.callerIp || '—', false, true],
                ['Declared IP', selected?.declaredIp || '—', false, true],
                ['Card', selected?.cardFingerprint || '—', true, true],
                ['Transaction', selected?.transactionId || '—', true, true],
                ['Order', selected?.orderId || '—', true, true],
              ] as const
            ).map(([label, value, mono, copyable]) => (
              <div key={label}>
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
                {copyable ? (
                  <DetailCopyValue value={value} mono={mono} />
                ) : (
                  <dd className="mt-0.5 break-words text-[13px] text-foreground">{value}</dd>
                )}
              </div>
            ))}
          </dl>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={!selected?.customerEmail || saving}
              onClick={() => blockFromDecision('EMAIL', selected?.customerEmail)}
            >
              Block this email for 24h
            </Button>
            <Button
              variant="outline"
              disabled={!selected?.cardHash || saving}
              onClick={() => blockFromDecision('CARD_HASH', selected?.cardHash)}
            >
              Block this card for 24h
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Why payments were flagged — moved off the page into a popup opened from the hero. */}
      <Dialog open={reasonsOpen} onOpenChange={setReasonsOpen}>
        <DialogContent className="max-w-xl gap-6 overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Why payments were flagged</DialogTitle>
            <DialogDescription>
              The rules that fired most across screened payments in {periodLabel}.
            </DialogDescription>
          </DialogHeader>
          {summary?.topReasons?.length ? (
            <div className="flex min-w-0 flex-col gap-5">
              {summary.topReasons.map((reason) => (
                <div key={reason.reason} className="min-w-0">
                  <div className="mb-1 flex min-w-0 items-start justify-between gap-3">
                    <span className="min-w-0 flex-1 whitespace-normal break-words text-[13px] text-foreground">
                      {reason.reason}
                    </span>
                    <span className="shrink-0 text-[13px] font-semibold tabular-nums text-muted-foreground">
                      {reason.count}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full max-w-full rounded-full bg-primary"
                      style={{
                        width: `${maxReasonCount ? Math.max(4, (reason.count / maxReasonCount) * 100) : 0}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No rules have fired in this period.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a blocklist entry</DialogTitle>
            <DialogDescription>
              Payments matching this value are refused before any acquirer is contacted. Leave the
              expiry blank to block permanently.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fraud-entry-type">Type</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger id="fraud-entry-type" aria-label="Entry type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTRY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fraud-entry-value">Value</Label>
              <Input
                id="fraud-entry-value"
                placeholder={
                  newType === 'EMAIL'
                    ? 'name@example.com'
                    : newType === 'IP'
                      ? '203.0.113.10'
                      : 'card fingerprint'
                }
                value={newValue}
                onChange={(event) => setNewValue(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fraud-entry-reason">Reason</Label>
              <Input
                id="fraud-entry-reason"
                placeholder="Why is this being blocked?"
                value={newReason}
                onChange={(event) => setNewReason(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fraud-entry-expiry">Expires in (hours)</Label>
              <Input
                id="fraud-entry-expiry"
                placeholder="Blank = permanent"
                value={newExpiryHours}
                onChange={(event) => setNewExpiryHours(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={saving}>
              {saving ? 'Adding…' : 'Add to blocklist'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Turning enforcement on starts refusing real payments — confirm before arming it. */}
      <Dialog open={confirmEnforceOpen} onOpenChange={setConfirmEnforceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn on enforce mode?</DialogTitle>
            <DialogDescription>
              Payments that score as a block will be <strong>refused in real time</strong>, before
              reaching an acquirer — this stops affecting customers only when you switch back to
              shadow. Review the recent blocked rows first to be sure the rules aren&rsquo;t catching
              genuine shoppers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmEnforceOpen(false)} disabled={modeSaving}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setConfirmEnforceOpen(false);
                await applyMode(true);
              }}
              disabled={modeSaving}
            >
              {modeSaving ? 'Turning on…' : 'Turn on enforce'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ListConsolePage>
  );
}
